import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi } from './client';

interface EtatRessource<T> {
  donnees: T | null;
  chargement: boolean;
  erreur: ErreurApi | null;
  recharger: () => Promise<void>;
}

/**
 * Chargement d'une ressource en lecture. Volontairement minimal : la
 * volumétrie du projet (une centaine de mandats) ne justifie pas un cache
 * client, et un cache mal invalidé afficherait des montants périmés.
 */
export function useRessource<T>(chemin: string | null): EtatRessource<T> {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [chargement, setChargement] = useState(chemin !== null);
  const [erreur, setErreur] = useState<ErreurApi | null>(null);

  const recharger = useCallback(async () => {
    if (chemin === null) return;
    setChargement(true);
    setErreur(null);
    try {
      setDonnees(await api.get<T>(chemin));
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e : new ErreurApi('ERREUR_INTERNE', String(e), 0));
      setDonnees(null);
    } finally {
      setChargement(false);
    }
  }, [chemin]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  return { donnees, chargement, erreur, recharger };
}

/** Regroupe les messages détaillés d'une erreur d'API en une phrase. */
export function messageErreur(erreur: ErreurApi | null): string | null {
  if (!erreur) return null;
  const details = erreur.details?.map((d) => d.message).filter(Boolean) ?? [];
  return details.length > 0 ? details.join(' ') : erreur.message;
}
